import { stringToDate } from 'modules/core/lib/dates'
import type Company from 'modules/core/store/models/Company'

import {
  employeeTaxResidenceValueTranslation,
  terminationReasonTranslation,
} from 'modules/bookkeepers/lib/dynamicTranslations'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import type {
  AvailableChange,
  FieldValue,
  IncidenceChangeItem,
  ValueModifier,
} from 'modules/payroll_bundle/lib/types'
import {
  booleanModifier,
  centsModifier,
  countryModifier,
  currencyModifier,
  dateModifier,
  frequencyModifier,
  weekdaysModifier,
  workingHoursfrequencyModifier,
} from 'modules/payroll_bundle/lib/utils'

const RESIDENCE_UPDATED = 'residence_updated'
const TERMINATION_REASON_TYPE_UPDATED = 'termination_reason_type_updated'

const AVAILABLE_LEAVE_CHANGES: AvailableChange[] = [
  {
    id: 'description_modified',
    field: 'description',
  },
  {
    id: 'start_date_modified',
    field: 'start_on',
    value_modifier: dateModifier,
  },
  {
    id: 'end_date_modified',
    field: 'finish_on',
    value_modifier: dateModifier,
  },
  {
    id: 'leave_type_modified',
    field: 'type',
  },
  {
    id: 'approval_modified',
    field: 'approved',
  },
  {
    id: 'deleted',
    field: 'deleted_at',
    value_modifier: dateModifier,
  },
] as const

const AVAILABLE_EMPLOYEE_DATA_CHANGES: AvailableChange[] = [
  {
    id: 'first_name_updated',
    field: 'first_name',
  },
  {
    id: 'last_name_updated',
    field: 'last_name',
  },
  {
    id: 'identifier_updated',
    field: 'identifier',
  },
  {
    id: 'identifier_type_updated',
    field: 'identifier_type',
  },
  {
    id: 'address_line_1_updated',
    field: 'address_line_1',
  },
  {
    id: 'address_line_2_updated',
    field: 'address_line_2',
  },
  {
    id: 'city_updated',
    field: 'city',
  },
  {
    id: 'postal_code_updated',
    field: 'postal_code',
  },
  {
    id: 'state_updated',
    field: 'state',
  },
  {
    id: 'country_updated',
    field: 'country',
    value_modifier: countryModifier,
  },
  {
    id: 'bank_number_updated',
    field: 'bank_number',
  },
  {
    id: 'swift_bic_updated',
    field: 'swift_bic',
  },
  {
    id: 'irpf_updated',
    field: 'irpf',
  },
  {
    id: 'irpf_cents_updated',
    field: 'irpf_cents',
    value_modifier: centsModifier,
  },
  {
    id: 'social_security_number_updated',
    field: 'social_security_number',
  },
  {
    id: 'nationality_updated',
    field: 'nationality',
    value_modifier: countryModifier,
  },
  {
    id: 'gender_updated',
    field: 'gender',
  },
  {
    id: RESIDENCE_UPDATED,
    field: 'is_resident',
  },
  {
    id: 'workplace_updated',
    field: 'location_name',
  },
] as const

const AVAILABLE_TERMINATION_CHANGES: AvailableChange[] = [
  {
    id: 'terminated_on_modified',
    field: 'terminated_on',
    value_modifier: dateModifier,
  },
  {
    id: TERMINATION_REASON_TYPE_UPDATED,
    field: 'termination_reason_type',
  },
] as const

const AVAILABLE_CONTRACT_CHANGES: AvailableChange[] = [
  {
    id: 'job_title_updated',
    field: 'job_title',
  },
  {
    id: 'job_level_updated',
    field: 'job_catalog_level_name',
  },
  {
    id: 'salary_amount_updated',
    field: 'salary_amount',
    value_modifier: currencyModifier,
  },
  {
    id: 'salary_frequency_updated',
    field: 'salary_frequency',
    value_modifier: frequencyModifier,
  },
  {
    id: 'working_hours_updated',
    field: 'working_hours',
    value_modifier: centsModifier,
  },
  {
    id: 'working_hours_frequency_updated',
    field: 'working_hours_frequency',
    value_modifier: workingHoursfrequencyModifier,
  },
  {
    id: 'working_week_days_updated',
    field: 'working_week_days',
    value_modifier: weekdaysModifier,
  },
  {
    id: 'max_legal_yearly_hours_updated',
    field: 'max_legal_yearly_hours',
    value_modifier: centsModifier,
  },
  {
    id: 'maximum_weekly_hours_updated',
    field: 'maximum_weekly_hours',
    value_modifier: centsModifier,
  },
  {
    id: 'es_job_description_updated',
    field: 'es_job_description',
  },
  {
    id: 'es_professional_category_updated',
    field: 'es_professional_category_name',
  },
  {
    id: 'contract_effective_on_updated',
    field: 'effective_on',
    value_modifier: dateModifier,
  },
  {
    id: 'contract_starts_on_updated',
    field: 'starts_on',
    value_modifier: dateModifier,
  },
  {
    id: 'contract_ends_on_updated',
    field: 'ends_on',
    value_modifier: dateModifier,
  },
  {
    id: 'es_education_level_updated',
    field: 'es_education_level',
  },
  {
    id: 'es_working_day_updated',
    field: 'es_working_day',
  },
  {
    id: 'es_contract_type_updated',
    field: 'es_contract_type',
  },
  {
    id: 'es_contract_observations_updated',
    field: 'es_contract_observations',
  },
  {
    id: 'es_contribution_type_updated',
    field: 'es_contribution_type',
  },
  {
    id: 'es_cno_occupation_updated',
    field: 'es_cno_occupation',
  },
  {
    id: 'es_regime_updated',
    field: 'es_regime',
  },
  {
    id: 'es_tariff_group_updated',
    field: 'es_tariff_group',
  },
  {
    id: 'es_classification_updated',
    field: 'es_classification',
  },
  {
    id: 'es_occupation_code_updated',
    field: 'es_occupation_code',
  },
  {
    id: 'es_a3innuva_job_position_updated',
    field: 'es_a3innuva_job_position',
  },
  {
    id: 'trial_period_ends_on_updated',
    field: 'trial_period_ends_on',
    value_modifier: dateModifier,
  },
  {
    id: 'fr_professional_category_updated',
    field: 'fr_professional_category_name',
  },
  {
    id: 'fr_forfait_jours_updated',
    field: 'fr_forfait_jours',
    value_modifier: booleanModifier,
  },
  {
    id: 'fr_jours_par_an_updated',
    field: 'fr_jours_par_an',
  },
]

const AVAILABLE_MANUAL_INCIDENCE_CHANGES: AvailableChange[] = [
  {
    id: 'description_modified',
    field: 'description',
  },
  {
    id: 'subject_updated',
    field: 'subject',
  },
  {
    id: 'effective_date_updated',
    field: 'incidence_effective_on',
    value_modifier: dateModifier,
  },
] as const

export const getAvailableChanges = (incidenceType: string): AvailableChange[] => {
  switch (incidenceType) {
    case INCIDENCE_TYPES.sick:
    case INCIDENCE_TYPES.parental:
    case INCIDENCE_TYPES.custom_leave:
      return AVAILABLE_LEAVE_CHANGES
    case INCIDENCE_TYPES.termination:
      return AVAILABLE_TERMINATION_CHANGES
    case INCIDENCE_TYPES.hiring:
      return AVAILABLE_EMPLOYEE_DATA_CHANGES.concat(AVAILABLE_CONTRACT_CHANGES)
    case INCIDENCE_TYPES.contract:
      return AVAILABLE_CONTRACT_CHANGES
    case INCIDENCE_TYPES.manual_incidence:
      return AVAILABLE_MANUAL_INCIDENCE_CHANGES
    default:
      return AVAILABLE_EMPLOYEE_DATA_CHANGES
  }
}

export const getModifiedValue = (
  field: string,
  attributes: Record<string, string> | undefined,
  company: Company,
  modifier?: ValueModifier
): FieldValue => {
  if (!attributes) return null

  const fieldValue = attributes[field]

  return modifier ? modifier({ fieldValue, company }) : fieldValue
}

export const parseChange = (
  currentValue: FieldValue,
  previousValue: FieldValue,
  change: AvailableChange,
  effectiveAt: string,
  author: string
) => {
  if (currentValue === previousValue) {
    return null
  }

  switch (change.id) {
    case RESIDENCE_UPDATED:
      return {
        id: change.id,
        currentValue: {
          value: employeeTaxResidenceValueTranslation(currentValue as boolean),
        },
        previousValue: {
          value:
            previousValue !== null
              ? employeeTaxResidenceValueTranslation(previousValue as boolean)
              : previousValue,
        },
        effectiveAt: stringToDate(effectiveAt, 'backend_datetime'),
      }
    case TERMINATION_REASON_TYPE_UPDATED:
      return {
        id: change.id,
        currentValue: {
          value: terminationReasonTranslation(currentValue as string),
        },
        previousValue: { value: previousValue },
        effectiveAt: stringToDate(effectiveAt, 'backend_datetime'),
      }
    default:
      return {
        id: change.id,
        previousValue: { value: previousValue },
        currentValue: { value: currentValue },
        effectiveAt: stringToDate(effectiveAt, 'backend_datetime'),
        changedBy: author,
      }
  }
}

export const CHANGE_CREATED = 'created'

export const sortChanges = (a: IncidenceChangeItem, b: IncidenceChangeItem) => {
  if (a.effectiveAt.isSame(b.effectiveAt)) {
    if (a.id === CHANGE_CREATED) {
      return -1
    }

    if (b.id === CHANGE_CREATED) {
      return 1
    }

    return 0
  } else {
    return a.effectiveAt.isAfter(b.effectiveAt) ? 1 : -1
  }
}
